var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Divider, Toolbar, AppBar, CssBaseline, Drawer, useMediaQuery, IconButton, useTheme, Hidden, Typography, Tooltip, } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccountCircle, Brightness4, Brightness7, Menu } from "@material-ui/icons";
import { Player } from "frontend/src/features/show-player";
import { paths } from "frontend/src/pages/paths";
import { Logo } from "frontend/src/ui/logo";
import { useViewer } from "frontend/src/domain/session";
import { useThemeMode } from "frontend/src/shared/theme-mode";
import { Navigation } from "./navigation";
import { Breadcrumbs } from "./breadcrumbs";
var drawerWidth = 260;
var useStyles = makeStyles(function (theme) {
    var _a, _b, _c;
    return ({
        toolbar: __assign(__assign({}, theme.mixins.toolbar), { minHeight: 64 }),
        title: (_a = {
                flexShrink: 0,
                maxWidth: "48vw",
                marginLeft: drawerWidth,
                color: theme.palette.type === "dark" ? "#fff" : "#2f343b",
                fontWeight: 500,
                textShadow: theme.palette.type === "dark"
                    ? "0 2px 18px rgba(0, 0, 0, 0.46)"
                    : "0 1px 14px rgba(255, 255, 255, 0.55)"
            },
            _a[theme.breakpoints.down("xs")] = {
                fontSize: 20,
                maxWidth: "42vw",
                marginLeft: 0,
            },
            _a[theme.breakpoints.down("sm")] = {
                marginLeft: 0,
            },
            _a),
        appBar: (_b = {
                position: "fixed",
                top: 0,
                zIndex: theme.zIndex.drawer + 1,
                marginLeft: drawerWidth,
                width: "calc(100% - " + drawerWidth + ")",
                color: theme.palette.type === "dark" ? "#fff" : "#2f343b",
                backgroundColor: "rgba(15, 21, 31, 0.26)",
                backdropFilter: "blur(22px) saturate(145%)",
                WebkitBackdropFilter: "blur(22px) saturate(145%)",
                boxShadow: "0 16px 40px rgba(0, 0, 0, 0.12)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.14)"
            },
            _b[theme.breakpoints.down("sm")] = {
                width: "100%",
                marginLeft: 0,
            },
            _b),
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
        },
        drawerPaper: {
            width: drawerWidth,
            color: theme.palette.type === "dark" ? "#fff" : "#2f343b",
            backgroundColor: "rgba(16, 23, 34, 0.28)",
            backdropFilter: "blur(26px) saturate(145%)",
            WebkitBackdropFilter: "blur(26px) saturate(145%)",
            borderRight: "1px solid rgba(255, 255, 255, 0.14)",
            boxShadow: "18px 0 42px rgba(0, 0, 0, 0.16)",
            zIndex: theme.zIndex.drawer + 2,
        },
        main: (_c = {
                flexGrow: 1,
                position: "relative",
                minHeight: "100vh",
                backgroundColor: theme.palette.type === "dark" ? "#101418" : "#eef3f5",
                padding: theme.spacing(3, 3, 0),
                paddingBottom: 140,
                width: "100%",
                overflow: "hidden",
                "&:before": {
                    content: '""',
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundImage: theme.palette.type === "dark"
                        ? "url('/images/client-dashboard-bg.jpg')"
                        : "url('/images/client-dashboard-bg-light.jpg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: theme.palette.type === "dark" ? 0.72 : 0.94,
                    transform: "scale(1.03)",
                    pointerEvents: "none",
                },
                "&:after": {
                    content: '""',
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    background: theme.palette.type === "dark"
                        ? "rgba(5, 9, 14, 0.24)"
                        : "rgba(14, 27, 42, 0.12)",
                    pointerEvents: "none",
                },
                "& > *": {
                    position: "relative",
                    zIndex: 1,
                },
                "& .MuiPaper-root": {
                    color: theme.palette.type === "dark" ? "#fff" : "#2f343b",
                    backgroundColor: theme.palette.type === "dark"
                        ? "rgba(20, 26, 38, 0.34)"
                        : "rgba(36, 43, 58, 0.24)",
                    backdropFilter: "blur(28px) saturate(155%)",
                    WebkitBackdropFilter: "blur(28px) saturate(155%)",
                    border: "1px solid rgba(255, 255, 255, 0.22)",
                    boxShadow: theme.palette.type === "dark"
                        ? "0 22px 48px rgba(0, 0, 0, 0.24)"
                        : "0 22px 48px rgba(0, 0, 0, 0.16)",
                },
                "& .MuiPaper-root .MuiTypography-colorTextSecondary, & .MuiPaper-root .MuiTypography-caption": {
                    color: theme.palette.type === "dark"
                        ? "rgba(255, 255, 255, 0.72)"
                        : "rgba(47, 52, 59, 0.68)",
                },
                "& .MuiPaper-root .MuiIconButton-root": {
                    color: theme.palette.type === "dark"
                        ? "rgba(255, 255, 255, 0.86)"
                        : "rgba(47, 52, 59, 0.78)",
                },
                "& .MuiTableContainer-root": {
                    backgroundColor: "rgba(25, 31, 43, 0.18)",
                },
                "& .MuiTable-root": {
                    borderCollapse: "separate",
                    borderSpacing: 0,
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                    color: theme.palette.type === "dark"
                        ? "rgba(255, 255, 255, 0.76)"
                        : "rgba(47, 52, 59, 0.76)",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.16)",
                },
                "& .MuiTableBody-root .MuiTableRow-root": {
                    color: theme.palette.type === "dark" ? "#fff" : "#2f343b",
                    backgroundColor: "rgba(255, 255, 255, 0.035) !important",
                },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
                    backgroundColor: "rgba(255, 255, 255, 0.075) !important",
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.12) !important",
                },
                "& .MuiTableCell-root": {
                    color: "inherit",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                    paddingTop: 8,
                    paddingBottom: 8,
                },
                "& .MuiTableCell-root a": {
                    color: "inherit",
                },
                "& .MuiTableSortLabel-root, & .MuiTableSortLabel-icon": {
                    color: theme.palette.type === "dark"
                        ? "rgba(255, 255, 255, 0.8) !important"
                        : "rgba(47, 52, 59, 0.8) !important",
                },
                "& .MuiTablePagination-root, & .MuiTablePagination-caption, & .MuiTablePagination-selectIcon": {
                    color: theme.palette.type === "dark"
                        ? "rgba(255, 255, 255, 0.82)"
                        : "rgba(47, 52, 59, 0.82)",
                }
            },
            _c[theme.breakpoints.down("xs")] = {
                padding: theme.spacing(2, 2, 0),
                paddingBottom: 120,
                "&:before, &:after": {
                    left: 0,
                },
            },
            _c),
        logoLink: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 72,
            padding: theme.spacing(1.5, 2),
        },
        userBadge: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            padding: theme.spacing(0.5, 1.25),
            color: theme.palette.type === "dark" ? "#fff" : "#2f343b",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            borderRadius: 6,
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(16px) saturate(145%)",
            WebkitBackdropFilter: "blur(16px) saturate(145%)",
            "& .MuiSvgIcon-root": {
                color: theme.palette.type === "dark"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(47, 52, 59, 0.82)",
            },
        },
    });
});
export var BusinessOwnerLayout = function (_a) {
    var _b;
    var children = _a.children, _c = _a.breadcrumbs, breadcrumbs = _c === void 0 ? true : _c;
    var theme = useTheme();
    var classes = useStyles();
    var user = useViewer().user;
    var showPermanentDrawer = useMediaQuery(theme.breakpoints.up("md"));
    var _d = React.useState(false), showDrawer = _d[0], setShowDrawer = _d[1];
    var _e = useThemeMode(), mode = _e.mode, toggleMode = _e.toggleMode;
    return (React.createElement(Box, { display: "flex" },
        React.createElement(CssBaseline, null),
        React.createElement(AppBar, { position: "fixed", color: "inherit", className: classes.appBar },
            React.createElement(Toolbar, { className: classes.toolbar },
                React.createElement(Hidden, { mdUp: true },
                    React.createElement(IconButton, { onClick: function () { return setShowDrawer(!showDrawer); } },
                        React.createElement(Menu, null))),
                React.createElement(Typography, { variant: "h5", component: "h1", noWrap: true, className: classes.title }, "\u041A\u0430\u0431\u0438\u043D\u0435\u0442 \u043A\u043B\u0438\u0435\u043D\u0442\u0430"),
                React.createElement(Box, { display: "flex", ml: "auto", alignItems: "center" },
                    React.createElement(Tooltip, { title: mode === "dark" ? "Включить светлый режим" : "Включить темный режим" },
                        React.createElement(IconButton, { onClick: toggleMode, color: "inherit" }, mode === "dark" ? React.createElement(Brightness7, null) : React.createElement(Brightness4, null))),
                    React.createElement(Box, { className: classes.userBadge, ml: 1 },
                        React.createElement(AccountCircle, { color: "action" }),
                        React.createElement(Typography, { variant: "body2", noWrap: true }, (_b = user === null || user === void 0 ? void 0 : user.email) !== null && _b !== void 0 ? _b : user === null || user === void 0 ? void 0 : user.name))))),
        React.createElement(Drawer, { variant: showPermanentDrawer ? "permanent" : "temporary", anchor: "left", classes: { root: classes.drawer, paper: classes.drawerPaper }, open: showDrawer, onClose: function () { return setShowDrawer(false); } },
            React.createElement(Box, null,
                React.createElement(RouterLink, { to: paths.business.owner.home(), className: classes.logoLink },
                    React.createElement(Logo, { width: "76%" }))),
            React.createElement(Divider, null),
            React.createElement(Navigation, null)),
        React.createElement(Box, { component: "main", className: classes.main },
            React.createElement(Toolbar, { className: classes.toolbar }),
            breadcrumbs && (React.createElement(Box, { mb: 2 },
                React.createElement(Breadcrumbs, null))),
            React.createElement(Box, { display: "flex", justifyContent: "center" },
                React.createElement(Box, { width: "100%", maxWidth: 1200 }, children))),
        React.createElement(Player, null)));
};
